import { decode, encode } from 'js-base64'
import { z } from 'zod'

import { identityProviders } from '@/auth/identity-provider'
import { RegistryResourceTypes } from '@/registry'
import { nanoid, nanoidSchema } from '@/utility'

/** Track platform initiated SAML request state */
export const samlStateSchema = z
  .object({
    uid: nanoidSchema,
    // AuthN request identifier
    requestId: z.string(),
    // IDP configuration identifier
    idpUid: nanoidSchema,
    // Random state
    relayState: z.string().min(20),
    // Redirect after sign in
    redirect: z.string().optional(),
    // Expiry
    expiresAt: z.number(),
  })
  .meta({ id: 'saml-state' })

export type SamlState = z.infer<typeof samlStateSchema>

export const ssoResourceSchema = z
  .enum([...RegistryResourceTypes, 'sync-project', 'project', 'publish'])
  .meta({ id: 'sso-resource' })

export type SSOResource = z.infer<typeof ssoResourceSchema>

export const ssoResourceLabels: Partial<Record<SSOResource, string>> = {
  'sync-project': 'Git Docs',
  'workspaces': 'Workspaces',
  'project': 'WYSIWYG Docs',
  'apis': 'Registry',
  'sdks': 'SDKs',
  'schemas': 'Schemas',
  'rules': 'Rules',
}

/** Relay state object schema */
export const relayStateSchema = z
  .object({
    /** Uniquely identifies a relay state */
    uid: nanoidSchema.default(nanoid).meta({ default: 'nanoid()' }),
    /** Identity provider metadata */
    idp: z.object({
      uid: nanoidSchema,
      provider: z.enum(identityProviders),
    }),
    /**
     * A resource indicates which specific product entity a
     * relay state is meant for.
     *
     * If optional, the relay state is meant to redirect
     * you to the platform (i.e. Dashboard URL) by default.
     */
    resource: z
      .object({
        /** Identify resource by UID */
        uid: nanoidSchema,
        /** Allowed product SSO connections */
        type: ssoResourceSchema,
      })
      .optional(),
  })
  .meta({ id: 'relay-state', default: 'nanoid()' })

export type RelayStateInput = z.input<typeof relayStateSchema>
export type RelayStateOutput = z.output<typeof relayStateSchema>

/**
 * Create a consistent dynamic relay state used for
 * any SSO connection with a product.
 *
 * Relay states are used to:
 *
 * 1. Identify a matching initiation session created by the platform
 * 2. Identify a link to a specific product (i.e. Guides, Registry)
 * 3. Handle redirects from IdP initiated sessions
 *
 * @throws Will throw if relay data is invalid
 */
export function generateRelayState(meta: RelayStateInput) {
  return encode(JSON.stringify(relayStateSchema.parse(meta)))
}

/**
 * Decode an base64url encoded SAML relay state.
 *
 * @returns The parsed relay state, or `null` if the state is invalid.
 */
export function decodeRelayState(state: string): RelayStateOutput | null {
  try {
    return relayStateSchema.parse(JSON.parse(decode(state)))
  } catch {
    return null
  }
}
